import { Box, Typography } from "@mui/material";
import type { CSSProperties } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { PASS_LINE_Y, standLeftX, standRightX } from "../utils/viewBoxLayout";
import { VisualizerTooltip } from "./VisualizerTooltip";

interface Props {
	fromPosition: number;
	toPosition: number;
	speedMps: number;
	active: boolean;
}

const PassLineTooltipContent = ({
	fromPosition,
	toPosition,
	speedMps,
	active,
}: Props) => (
	<Box sx={{ minWidth: 160, fontSize: "0.75rem" }}>
		<Box
			sx={{
				pb: 0.5,
				mb: 0.5,
				borderBottom: `1px solid ${mesfColors.grey[200]}`,
			}}
		>
			<Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
				Cascade {fromPosition} → {toPosition}
			</Typography>
		</Box>
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
				<Typography
					sx={{
						fontSize: "0.7rem",
						color: mesfColors.text.secondary,
						textTransform: "uppercase",
					}}
				>
					Speed
				</Typography>
				<Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
					{speedMps.toFixed(1)} m/s
				</Typography>
			</Box>
			{active ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						mt: 0.25,
					}}
				>
					<Box
						sx={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							bgcolor: mesfColors.success.main,
						}}
					/>
					<Typography
						sx={{
							fontSize: "0.7rem",
							color: mesfColors.success.dark,
							fontWeight: 600,
						}}
					>
						Billet present
					</Typography>
				</Box>
			) : null}
		</Box>
	</Box>
);

export const PassLine = ({
	fromPosition,
	toPosition,
	speedMps,
	active,
}: Props) => {
	const x1 = standRightX(fromPosition);
	const x2 = standLeftX(toPosition);
	const flowing = active && speedMps > 0;

	const durationSec = speedMps > 0 ? Math.max(0.3, 2 / speedMps) : 0;

	const stroke = active ? mesfColors.success.main : mesfColors.grey[400];
	const strokeWidth = flowing ? 3 : 2;

	const flowingStyle: CSSProperties | undefined = flowing
		? { animationDuration: `${durationSec}s` }
		: undefined;

	return (
		<VisualizerTooltip
			title={
				<PassLineTooltipContent
					fromPosition={fromPosition}
					toPosition={toPosition}
					speedMps={speedMps}
					active={active}
				/>
			}
		>
			<g>
				{/* Invisible wider hit area so the tooltip triggers easily even
				    when the visible line is dashed. */}
				<line
					x1={x1}
					y1={PASS_LINE_Y}
					x2={x2}
					y2={PASS_LINE_Y}
					stroke="transparent"
					strokeWidth={14}
				/>
				<line
					x1={x1}
					y1={PASS_LINE_Y}
					x2={x2}
					y2={PASS_LINE_Y}
					stroke={stroke}
					strokeWidth={strokeWidth}
					strokeDasharray="6 4"
					className={flowing ? "visualizer-pass-flow" : undefined}
					style={flowingStyle}
				/>
			</g>
		</VisualizerTooltip>
	);
};
